<template>
  <nav ref="navRef" class="nav-tabs">
    <a v-for="tab in tabs" :key="tab.key" :href="tab.to" class="tab" :class="{ active: tab.key === activeId }"
      :ref="(el) => setTabRef(el, tab.key)">
      {{ tab.label }}
    </a>
  </nav>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";

type TabItem = {
  key: string;
  label: string;
  to: string;
};

const props = withDefaults(
  defineProps<{
    tabs?: TabItem[];
    activeId?: string;
  }>(),
  {
    tabs: () => [
      { key: "0", label: "最新", to: "/" },
      { key: "2", label: "影视", to: "/category/2" },
      { key: "7", label: "动漫", to: "/category/7" },
      { key: "8", label: "书籍", to: "/category/8" },
      { key: "9", label: "软件", to: "/category/9" },
      { key: "10", label: "教程", to: "/category/10" },
      { key: "11", label: "游戏", to: "/category/11" },
      { key: "12", label: "系统", to: "/category/12" },
      { key: "13", label: "图片", to: "/category/13" },
      { key: "14", label: "音乐", to: "/category/14" },
      { key: "15", label: "素材", to: "/category/15" },
      { key: "16", label: "其他", to: "/category/16" },
    ],
    activeId: "0",
  }
);

const navRef = ref<HTMLElement | null>(null);
const tabRefs = ref(new Map<string, HTMLElement>());

const setTabRef = (el: Element | null, key: string) => {
  if (!el) {
    tabRefs.value.delete(key);
    return;
  }
  tabRefs.value.set(key, el as HTMLElement);
};

const scrollActiveIntoView = () => {
  const container = navRef.value;
  if (!container) return;
  const activeKey = props.activeId ?? "0";
  const activeEl = tabRefs.value.get(activeKey);
  if (!activeEl) return;
  const target = activeEl.offsetLeft - (container.clientWidth - activeEl.clientWidth) / 2;
  container.scrollTo({ left: Math.max(0, target) });
};

watch(
  () => props.activeId,
  () => {
    nextTick(scrollActiveIntoView);
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(scrollActiveIntoView);
});
</script>

<style scoped>
.nav-tabs {
  display: flex;
  align-items: center;
  gap: 14px;
  position: fixed;
  left: 0;
  right: 0;
  top: var(--app-header-height, 64px);
  height: var(--app-navtabs-height, 44px);
  box-sizing: border-box;
  padding: 10px 14px 6px;
  background: #fff;
  overflow-x: auto;
  scrollbar-width: none;
  z-index: 90;
}

.nav-tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  text-decoration: none;
  border: none;
  background: transparent;
  color: #666;
  padding: 0;
  font-size: 15px;
  position: relative;
  flex: 0 0 auto;
  min-width: 44px;
}

.tab.active {
  color: #fb7299;
  font-weight: 700;
}

.tab.active::after {
  content: "";
  position: absolute;
  left: 35%;
  transform: translateX(-50%);
  width: 60%;
  bottom: -6px;
  height: 3px;
  border-radius: 2px;
  background: #fb7299;
}

.tab.more {
  font-size: 18px;
}

:global(:root) {
  --app-navtabs-height: 44px;
}
</style>
